/**
 * Presence participation manager.
 *
 * Presence runs on the single shared SignalR connection, so "am I present on / observing
 * record X" is a connection-level fact, not a per-component one. Multiple surfaces may
 * reference the same record (a detail header viewing it, a list row watching it); they must
 * share one group membership so that one unmounting does not tear down the other.
 *
 * Two independent ref-counts per record:
 * - subscription (view or watch) governs group membership (message delivery),
 * - viewer (view only) governs member status (being counted/broadcast as present).
 *
 * Transitions translate to hub invokes:
 * - first viewer  -> JoinPresence   (becomes a member; the join also (re)adds to the group)
 * - last viewer   -> LeavePresence  (stops being a member; stays in group while watched)
 * - first watch-only subscription -> WatchPresence (group delivery, never a member)
 * - last subscription -> StopWatchingPresence (leaves the group entirely)
 *
 * Invokes are advisory: attempted only while Connected; transport errors are swallowed and
 * self-heal on the next heartbeat / reconnect re-join.
 */

import { HubConnectionState, type HubConnection } from "@microsoft/signalr";

import {
    presenceRefKey,
    PRESENCE_METHODS,
    type PresenceHold,
    type PresenceRef,
} from "./presenceWire";

const SYNC_REPLY_DEBOUNCE_MS = 1_500;

interface CountedRef {
    readonly ref: PresenceRef;
    count: number;
}

export interface PresenceParticipation {
    /** Point the manager at the current connection and re-establish all holds if connected. */
    syncConnection(connection: HubConnection | null): void;
    /** Acquire a hold on a record (ref-counted by hold kind). */
    acquire(ref: PresenceRef, hold: PresenceHold): void;
    /** Release a hold on a record (ref-counted by hold kind). */
    release(ref: PresenceRef, hold: PresenceHold): void;
    /** Heartbeat: re-announce every record we are a viewer of. */
    announceAll(): void;
    /** Reconnect: re-establish group membership and member status for every held record. */
    rejoinAll(): void;
    /** Reply to a server sync request by re-announcing the ref (debounced) if we view it. */
    replyToSync(ref: PresenceRef): void;
    /** Records we are counted present on (test/diagnostic surface). */
    viewerRefs(): readonly PresenceRef[];
    /** Records we hold a group subscription on (test/diagnostic surface). */
    subscriptionRefs(): readonly PresenceRef[];
}

export function createPresenceParticipation(): PresenceParticipation {
    const subscriptions = new Map<string, CountedRef>();
    const viewers = new Map<string, CountedRef>();
    const syncTimers = new Map<string, ReturnType<typeof setTimeout>>();
    let connection: HubConnection | null = null;

    function invokeSafe(method: string, ref: PresenceRef): void {
        const active = connection;
        if (active === null || active.state !== HubConnectionState.Connected) {
            return;
        }
        void active.invoke(method, ref.entityType, ref.entityId).catch((error: unknown) => {
            console.warn(`Presence ${method} failed`, error);
        });
    }

    function increment(map: Map<string, CountedRef>, key: string, ref: PresenceRef): boolean {
        const existing = map.get(key);
        if (existing !== undefined) {
            existing.count += 1;
            return false;
        }
        map.set(key, { ref, count: 1 });
        return true;
    }

    function decrement(map: Map<string, CountedRef>, key: string): boolean {
        const existing = map.get(key);
        if (existing === undefined) {
            return false;
        }
        existing.count -= 1;
        if (existing.count > 0) {
            return false;
        }
        map.delete(key);
        return true;
    }

    function reestablish(): void {
        // A viewer's Join re-adds the group too, so only watch-only subscriptions need Watch.
        for (const [key, { ref }] of subscriptions) {
            if (viewers.has(key)) {
                invokeSafe(PRESENCE_METHODS.join, ref);
            } else {
                invokeSafe(PRESENCE_METHODS.watch, ref);
            }
        }
    }

    return {
        syncConnection(next) {
            connection = next;
            reestablish();
        },

        acquire(ref, hold) {
            const key = presenceRefKey(ref.entityType, ref.entityId);
            const firstSubscription = increment(subscriptions, key, ref);

            if (hold === "view") {
                if (increment(viewers, key, ref)) {
                    invokeSafe(PRESENCE_METHODS.join, ref);
                }
                return;
            }

            // Watch-only: ensure group delivery without becoming a member. A viewer's Join already
            // covers the group, so only invoke Watch when this is the first subscription.
            if (firstSubscription) {
                invokeSafe(PRESENCE_METHODS.watch, ref);
            }
        },

        release(ref, hold) {
            const key = presenceRefKey(ref.entityType, ref.entityId);

            if (hold === "view" && decrement(viewers, key)) {
                invokeSafe(PRESENCE_METHODS.leave, ref);
            }

            if (decrement(subscriptions, key)) {
                const pending = syncTimers.get(key);
                if (pending !== undefined) {
                    clearTimeout(pending);
                    syncTimers.delete(key);
                }
                invokeSafe(PRESENCE_METHODS.stopWatching, ref);
            }
        },

        announceAll() {
            for (const { ref } of viewers.values()) {
                invokeSafe(PRESENCE_METHODS.announce, ref);
            }
        },

        rejoinAll() {
            reestablish();
        },

        replyToSync(ref) {
            const key = presenceRefKey(ref.entityType, ref.entityId);
            if (!viewers.has(key)) {
                return;
            }
            if (syncTimers.has(key)) {
                return; // already scheduled; debounce
            }
            const timer = setTimeout(() => {
                syncTimers.delete(key);
                invokeSafe(PRESENCE_METHODS.announce, ref);
            }, SYNC_REPLY_DEBOUNCE_MS);
            syncTimers.set(key, timer);
        },

        viewerRefs() {
            return [...viewers.values()].map((entry) => entry.ref);
        },

        subscriptionRefs() {
            return [...subscriptions.values()].map((entry) => entry.ref);
        },
    };
}

/** Process-wide participation manager, wired to the connection by the presence channel. */
export const presenceParticipation = createPresenceParticipation();
